import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ShoppingBag, X, Plus, Minus } from 'lucide-react';
import { Button } from './ui/button';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { getLineGross } from '../utils/pricing';
import { useTiffinOfTheDay } from '../hooks/useTiffinOfTheDay';
import { allocateTiffinUpsells } from '../utils/tiffinPlan';

/** The selected store's id from localStorage (null when none / unreadable). */
function savedBranchId() {
  try { return JSON.parse(localStorage.getItem('selectedBranch') || 'null')?.id || null; } catch { return null; }
}

/**
 * Slide-up cart preview; auto-closes after 4 s; shows up to 3 items with inline quantity controls.
 */
export default function CartDrawer() {
  const { cart, cartDrawerOpen, setCartDrawerOpen, cartDrawerNonce, updateQuantityAt, getCartCount } = useCart();
  const [menuItems, setMenuItems] = useState([]);
  const navigate = useNavigate();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  // Business Rule #58 — today's Tiffin of the Day pairing, so the preview shows
  // the paired item at its special price (same allocation as the Cart).
  const { today: tiffinToday } = useTiffinOfTheDay(savedBranchId());

  useEffect(() => {
    if (cartDrawerOpen && cart.length > 0) {
      const ids = cart.map(c => c.menu_item_id).filter(Boolean);
      if (ids.length) {
        // Business Rule #27 — scope to current branch (matches Cart/Checkout).
        let savedBranch = null;
        try { savedBranch = JSON.parse(localStorage.getItem('selectedBranch') || 'null'); } catch { /* noop */ }
        const url = savedBranch?.id
          ? `${API}/menu/items/batch?branch_id=${encodeURIComponent(savedBranch.id)}`
          : `${API}/menu/items/batch`;
        axios.post(url, { ids }).then(res => setMenuItems(res.data)).catch(() => {});
      }
    }
  }, [cartDrawerOpen, cart.length, API]);

  // Auto-close after 4 seconds. Keyed on `cartDrawerNonce` (bumped by every
  // add) so a second add RESTARTS the full 4s window — `setCartDrawerOpen(true)`
  // is a no-op when the drawer is already open, so without the nonce this effect
  // wouldn't re-run and the later add rode the first add's countdown (closing
  // almost immediately → the "floating cart doesn't come again" report).
  useEffect(() => {
    if (!cartDrawerOpen) return;
    const timer = setTimeout(() => setCartDrawerOpen(false), 4000);
    return () => clearTimeout(timer);
  }, [cartDrawerOpen, cartDrawerNonce, setCartDrawerOpen]);

  if (!cartDrawerOpen || cart.length === 0) return null;

  const getItem = (id) => menuItems.find(m => m.id === id);
  const menuMap = Object.fromEntries(menuItems.map(m => [m.id, m]));
  const tiffinAlloc = tiffinToday.active
    ? allocateTiffinUpsells(cart, menuMap, tiffinToday.picks)
    : { lines: {} };
  // Same line maths as the Cart: live size + extras (a daily-offer line is the
  // offer price + extras), less any Tiffin of the Day pairing saving. Before the
  // menu rows arrive, fall back to the stored prices.
  const lineTotal = (item, index) => {
    const mi = getItem(item.menu_item_id);
    if (!mi) {
      const basePrice = item.offer_price != null ? item.offer_price : 0;
      return (basePrice + (item.customization_price || 0)) * item.quantity;
    }
    return getLineGross(item, mi) - (tiffinAlloc.lines[index]?.saving || 0);
  };
  const total = cart.reduce((sum, item, index) => sum + lineTotal(item, index), 0);

  return (
    <div
      className="fixed bottom-16 left-0 right-0 z-[48] px-3 animate-in slide-in-from-bottom-4 duration-300"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="cart-drawer"
    >
      <div className="bg-primary text-primary-foreground rounded-2xl shadow-2xl overflow-hidden max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-3 pb-1">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" />
            <span className="text-sm font-semibold">{getCartCount()} item{getCartCount() > 1 ? 's' : ''} added</span>
          </div>
          <button onClick={() => setCartDrawerOpen(false)} className="p-1 hover:bg-white/10 rounded-full" data-testid="cart-drawer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Items preview (max 3) */}
        <div className="px-4 pb-2 space-y-1.5 max-h-36 overflow-y-auto">
          {cart.slice(0, 3).map((item, i) => {
            const mi = getItem(item.menu_item_id);
            return (
              <div key={i} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate flex-1 text-primary-foreground/90">{mi?.name || 'Item'}</span>
                <div className="flex items-center gap-1.5 bg-white/10 rounded-full px-1.5 py-0.5">
                  <button onClick={() => updateQuantityAt(i, item.quantity - 1)} className="p-0.5">
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="text-xs font-bold w-4 text-center">{item.quantity}</span>
                  <button onClick={() => updateQuantityAt(i, item.quantity + 1)} className="p-0.5">
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <span className="text-xs font-medium whitespace-nowrap">
                  {'\u20B9'}{lineTotal(item, i).toFixed(0)}
                </span>
              </div>
            );
          })}
          {cart.length > 3 && (
            <p className="text-xs text-primary-foreground/60 text-center">+{cart.length - 3} more item{cart.length - 3 > 1 ? 's' : ''}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-accent">
          <span className="font-bold text-accent-foreground">{'\u20B9'}{total.toFixed(0)}</span>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-full px-5 text-xs font-bold"
            onClick={() => { setCartDrawerOpen(false); navigate('/cart'); }}
            data-testid="cart-drawer-view-cart"
          >
            View Cart
          </Button>
        </div>
      </div>
    </div>
  );
}
